import React, { useEffect, useMemo } from 'react';
import { useHistory, useRouteMatch, generatePath } from 'react-router-dom';
import { Layout } from 'antd';
import Loading from 'components/Loading';
import { useLocalStorageState } from 'ahooks';
import equal from 'fast-deep-equal/react';
import useDataSource from 'hooks/useDataSource';
import Sidebar from './Sidebar';
import Routes from './Routes';
import { routesToMenuItems } from './utils';

const { Content } = Layout;

const RoutedLayout = ({
  routes,
  sidebar,
  className,
  loading,
  children,
}) => {
  const history = useHistory();
  const { url, params: baseParams } = useRouteMatch();
  const [storedParams, setStoredParams] = useLocalStorageState(`storedParams-${url}`);
  const { hasDataSourcePermission } = useDataSource();

  const path = `${url}/:page?/:subpage?`;

  const { params } = useRouteMatch(path);

  const hasAccessToRoute = r => !r.dataSourcePermission || hasDataSourcePermission(r.dataSourcePermission);

  const mapRoute = (route, paramValues) => {
    const routePath = generatePath(route.link || path, {
      ...baseParams,
      ...paramValues,
      ...route.params,
    });

    return {
      ...route,
      ...paramValues,
      path: routePath,
      children: route.children?.filter(hasAccessToRoute).map(r => mapRoute(r, { ...paramValues, subpage: r.path })),
    };
  };

  const mappedRoutes = useMemo(() => routes?.filter(hasAccessToRoute).map(r => mapRoute(r, { page: r.path })), [routes]);

  useEffect(() => {
    if (params.page) {
      if (!equal(params, storedParams) && !params.page === 'settings') {
        setStoredParams(params);
      }
    } else if (storedParams && storedParams.page && mappedRoutes.find(r => r.page === storedParams.page)) {
      history.replace(generatePath(path, storedParams));
    } else {
      const firstRoute = mappedRoutes.find(r => r.label && r.path);
      if (firstRoute) {
        history.replace(firstRoute.children?.find(r => r.path)?.path || firstRoute.path);
      }
    }
  }, [params, history, path, mappedRoutes, storedParams, setStoredParams]);

  const menu = useMemo(() => ({
    items: routesToMenuItems({ path, routes: mappedRoutes }),
    defaultOpenKeys: mappedRoutes?.filter(r => r.defaultOpen)?.map(r => r.path),
  }), [path, mappedRoutes]);

  return (
    <Layout className={className}>
      {sidebar && <Sidebar menu={menu} {...(sidebar !== true ? sidebar : {})} />}
      <Content>
        {loading ? <Loading /> : (
          children || <Routes routes={mappedRoutes} />
        )}
      </Content>
    </Layout>
  );
};

export default RoutedLayout;
